import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

import { RuteroMoney } from '@bandido/shared/types';

import { formatMoney } from '../money';
import { useAudience } from '../audience';

export interface PriceBlockPricing {
  net: RuteroMoney;
  final: RuteroMoney;
  /** El precio del proveedor YA es PVP: no hay margen que mostrar. */
  isGross?: boolean;
}

interface NetNoteProps {
  pricing: PriceBlockPricing;
  /**
   * Qué decir cuando el proveedor es comisionable. Por defecto un chip; autos
   * lo reemplaza por su explicación de que la comisión se liquida aparte.
   */
  grossNote?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * El neto escrito, o `undefined` si esta audiencia no lo ve. Es LA decisión
 * —un solo lugar—, separada de dónde se dibuja: `PriceBlock` lo pone en la
 * misma línea que la unidad para no gastar un renglón, y `NetNote` lo dibuja
 * suelto para quien necesite el bloque por su cuenta.
 */
export function useNetText(pricing: PriceBlockPricing): string | undefined {
  const { t } = useTranslation();
  const { isAgent } = useAudience();

  if (!isAgent || pricing.isGross) return undefined;
  return t('travelServices.netPrice', { price: formatMoney(pricing.net) });
}

/**
 * La línea que revela el margen. Quien necesite otro layout usa `useNetText`,
 * no vuelve a escribir la condición.
 */
export function NetNote({ pricing, grossNote, sx }: NetNoteProps) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  const netText = useNetText(pricing);

  if (!isAgent) return null;

  if (pricing.isGross) {
    // `undefined` es "no me dijeron nada, poné el aviso genérico"; `null` es
    // una decisión del caller: el aviso ya está dicho en otro lado de la card
    // y repetirlo por fila lo convierte en ruido.
    return grossNote !== undefined ? (
      <>{grossNote}</>
    ) : (
      <Chip size="small" sx={{ mt: 0.5, ...sx }} label={t('travelServices.grossPrice')} />
    );
  }

  return (
    <Typography variant="caption" color="text.secondary" display="block" sx={sx}>
      {netText}
    </Typography>
  );
}

interface Props {
  pricing: PriceBlockPricing;
  /** Qué cubre el precio: "total del vehículo", "por 3 días". */
  unit?: ReactNode;
  /**
   * "desde": el importe es el más barato de varias tarifas y no el precio
   * final. Va PEGADO al número y antes que él —es un calificador, no una
   * aclaración— así nadie lee el importe como cerrado y después vuelve a
   * leer. En su propio renglón costaba una línea entera de la columna de
   * decisión por una palabra, y esa columna es la que fija el alto de la card.
   */
  fromLabel?: string;
  /**
   * Aclaraciones que ven las DOS audiencias (el unitario del que sale el
   * total, por ejemplo). Van como texto ya armado: este bloque es genérico y
   * no conoce las claves de i18n de ningún vertical.
   */
  notes?: ReactNode;
  /** Aclaraciones solo para el asesor (PVP sugerido del proveedor). */
  agentNotes?: ReactNode;
  /** Qué decir cuando el proveedor es comisionable (ver `NetNote`). */
  grossNote?: ReactNode;
  /** Un importe en 0 puede ser real (tour gratuito, infante que no paga). */
  zeroLabel?: string;
  align?: 'left' | 'right';
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/**
 * El precio de una oferta. Todo lo que se le muestra de más al asesor —neto,
 * aviso de comisión, PVP sugerido— cuelga de `useAudience()`, así abrir una
 * superficie minorista es cambiar un flag y no cazar importes por diez cards.
 *
 * El precio de venta se muestra SIEMPRE y con la misma jerarquía en las dos
 * audiencias: es el número que se compara entre proveedores.
 */
export function PriceBlock({
  pricing,
  unit,
  fromLabel,
  notes,
  agentNotes,
  grossNote,
  zeroLabel,
  align = 'right',
  size = 'medium',
  sx,
}: Props) {
  const { isAgent } = useAudience();
  const netText = useNetText(pricing);

  /**
   * Las aclaraciones al cuerpo del wireframe (11,5 px) y con el interlineado
   * apretado: son cuatro renglones posibles y este bloque es el que fija el
   * alto de la card en las nueve verticales, con la densidad como criterio de
   * aceptación. A `variant="caption"` (12 px, interlineado 1,66) cada
   * aclaración costaba 20 px.
   */
  const caption = (content: ReactNode) => (
    <Typography
      color="text.secondary"
      display="block"
      sx={{ fontSize: 11.5, lineHeight: 1.25 }}
    >
      {content}
    </Typography>
  );

  const isFree = zeroLabel && pricing.final.amountMinor === 0;

  return (
    <Box sx={{ textAlign: align, ...sx }}>
      <Typography
        variant={size === 'small' ? 'body2' : undefined}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
          ...(size === 'small' ? {} : { fontSize: 19, fontWeight: 650 }),
        }}
      >
        {fromLabel && (
          <Box
            component="span"
            color="text.secondary"
            sx={{ fontSize: 11, fontWeight: 400, mr: 0.5 }}
          >
            {fromLabel}
          </Box>
        )}
        {isFree ? zeroLabel : formatMoney(pricing.final)}
      </Typography>

      {/* Unidad y neto comparten renglón —"por persona en doble · Neto EUR
          562"—: dicen cosas distintas del MISMO importe y separarlos gastaba
          dos líneas para dos frases de tres palabras. Es como lo escribe el
          wireframe en las filas de salidas. */}
      {(unit || netText) && caption(
        <>
          {unit}
          {unit && netText && (
            <Box component="span" color="text.disabled" sx={{ mx: 0.5 }}>
              ·
            </Box>
          )}
          {netText}
        </>,
      )}

      {/* El aviso de comisionable no es un número y no se puede fusionar con
          nada: cuando el proveedor es PVP, sigue en su propio renglón. */}
      {pricing.isGross && <NetNote pricing={pricing} grossNote={grossNote} />}

      {/* El unitario explica de dónde sale el total: lo ven las dos audiencias
          porque no revela margen. */}
      {notes && caption(notes)}

      {isAgent && agentNotes && caption(agentNotes)}
    </Box>
  );
}
